import { useState, useCallback, useMemo } from "react";
import { useAuth, useToast } from "./";
import { AccountForecastService } from "../services/AccountForecastService";

export const useAccountForecast = () => {
  const { apiClient } = useAuth();
  const showToast = useToast();
  const [forecastData, setForecastData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Create service instance only when dependencies change
  const forecastService = useMemo(() => {
    return new AccountForecastService(apiClient, showToast);
  }, [apiClient, showToast]);

  const fetchForecastData = useCallback(
    async (accountId) => {
      if (!accountId) return null;

      setLoading(true);
      try {
        const data = await forecastService.getAccountForecastData(accountId);
        setForecastData(data);
        return data;
      } catch (error) {
        // Error handling is done in the service
        return null;
      } finally {
        setLoading(false);
      }
    },
    [forecastService],
  );

  const updateForecastNote = useCallback(
    async (accountId, note) => {
      try {
        await forecastService.updateForecastNote(accountId, note);

        // Update local state with the new note
        setForecastData((prev) =>
          prev
            ? {
                ...prev,
                account: {
                  ...prev.account,
                  forecast_note: note,
                },
                insights: {
                  ...prev.insights,
                  forecast_note: note,
                },
              }
            : null,
        );

        return true;
      } catch (error) {
        // Error handling is done in the service
        return false;
      }
    },
    [forecastService],
  );

  const refreshData = useCallback(
    async (accountId) => {
      return await fetchForecastData(accountId);
    },
    [fetchForecastData],
  );

  return {
    forecastData,
    loading,
    fetchForecastData,
    updateForecastNote,
    refreshData,
    // Helper methods from service
    getForecastStatusDisplay: AccountForecastService.getForecastStatusDisplay,
  };
};
